import axios from 'axios'
import VueAxios from 'vue-axios'
import { toast } from 'vue3-toastify'
import qs from 'qs'
import { useLegalModalStore } from '@/stores/legalModal'
import settings from '@/settings'

axios.defaults.baseURL = settings.API_URL

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function initAxios({ app, router, pinia, i18n, settings }) {
  const axios_include_timezone = axios.create()

  const axiosRequestHandler = (config) => {
    const { $cookies } = app.config.globalProperties
    config.headers['Authorization'] = 'Bearer ' + $cookies.get('token')

    if (!config.headers['X-Debug-Request-Id']) {
      config.headers['X-Debug-Request-Id'] = generateRequestId()
    }

    return config
  }

  const axiosErrorHandler = (error) => {
    if (error.name === 'AbortError' || axios.isCancel(error)) {
      console.log('Request was aborted. Ignoring error.')
      return Promise.reject(error)
    }

    const resp = error.response || null

    if (resp && resp.data?.code === 400303) {
      toast.error(resp.data?.message, { multiple: false })
      const legalModalStore = useLegalModalStore(pinia)
      legalModalStore.showModal()
      return Promise.reject(error)
    }

    if (resp && resp.status === 429) {
      try {
        window.dispatchEvent(new CustomEvent('api-429'))
      } catch (e) {
        const evt = document.createEvent('Event')
        evt.initEvent('api-429', true, true)
        window.dispatchEvent(evt)
      }
      toast.error('Too many requests. Please try again later.')
    }

    if (error.config.errorHandling === false) {
      // intentionally empty
    } else if (error.code === 'ERR_NETWORK') {
      const t = i18n.global.t
      const toastMsg = `<div class="flex flex-col items-start">
                          <span class="font-bold text-base">${t('networkErrorToast')}</span>
                          <span>${t('connectionProblem')}</span>
                          <div>
                            <span>${t('tryLater')}</span>
                            <button class="text-[#0082ff]" onclick="window.location.reload()">
                              ${t('reload')}
                            </button>
                          </div>
                        </div>`
      toast(toastMsg, {
        autoClose: false,
        multiple: false,
        closeOnClick: false,
        dangerouslyHTMLString: true,
        theme: 'custom'
      })
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request Timeout!')
    } else if (error.response?.status === 401) {
      const { $cookies } = app.config.globalProperties
      $cookies.remove('token')
      if (
        router.currentRoute.value.path !== '/' &&
        router.currentRoute.value.meta.needAuth !== false
      ) {
        router.push({ name: 'Login' })
      }
    } else if (error.response?.status >= 500 && error.response?.status < 600) {
      toast.error('Server Error!')
    } else if (
      error.response?.data instanceof Object &&
      'code' in error.response.data &&
      'ignoreErrorCodes' in error.config &&
      error.config.ignoreErrorCodes.includes(error.response.data.code)
    ) {
      // intentionally empty
    } else if ([400, 402, 403, 404, 405, 406].includes(error.response?.status)) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message, {
          position: toast.POSITION.TOP_CENTER,
          theme: toast.THEME.COLORED,
          toastId: error.response.data.message
        })
      } else {
        toast.error('Bad Request!')
      }
    }

    return Promise.reject(error)
  }

  axios.interceptors.response.use((response) => response, axiosErrorHandler)
  axios.interceptors.request.use(axiosRequestHandler)

  axios.defaults.paramsSerializer = (params) => {
    return qs.stringify(app.config.globalProperties.$filters.removeNullAndEmptyValues(params), {
      encode: false,
      allowDots: true
    })
  }

  axios_include_timezone.defaults.paramsSerializer = axios.defaults.paramsSerializer
  axios_include_timezone.defaults.baseURL = axios.defaults.baseURL

  app.config.globalProperties.axios_include_timezone = axios_include_timezone

  axios_include_timezone.interceptors.request.use((config) => {
    var config = axiosRequestHandler(config)
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    config.headers['UserTimeZone'] = userTimeZone
    config.params = {
      ...config.params,
      UserTimeZone: userTimeZone
    }
    return config
  })

  axios_include_timezone.interceptors.response.use((response) => response, axiosErrorHandler)

  app.use(VueAxios, axios)
  app.use(VueAxios, axios_include_timezone)

  return { axios, axios_include_timezone }
}
